import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, BarChart3, Calendar, CheckCircle2, Copy, DollarSign, ExternalLink, TrendingDown, Users, X } from 'lucide-react';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { summarizePeriod, toDate, toDateKey } from '../../utils/reservationAnalytics';
import { groupClientsByIdentity } from '../../utils/clientIdentity';
import LoadingState from './LoadingState';
import './Dashboard.css';

const monthRange = () => {
  const now = new Date();
  return {
    start: toDateKey(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: toDateKey(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  };
};

const presetRange = (preset) => {
  const today = new Date();
  if (preset === 'today') return { start: toDateKey(today), end: toDateKey(today) };
  if (preset === '7days' || preset === '30days') {
    const start = new Date(today);
    start.setDate(start.getDate() - (preset === '7days' ? 6 : 29));
    return { start: toDateKey(start), end: toDateKey(today) };
  }
  return monthRange();
};

const Dashboard = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const navigate = useNavigate();
  const initialRange = monthRange();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [services, setServices] = useState([]);
  const [activeClients, setActiveClients] = useState(0);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [companyLink, setCompanyLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const companyId = userData?.companyId || userData?.currentCompanyId;

  useEffect(() => {
    if (!selectedReservation) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setSelectedReservation(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [selectedReservation]);

  const loadCompanyLink = useCallback(async () => {
    if (!companyId) return;
    try {
      const companySnap = await getDoc(doc(db, 'companies', companyId));
      if (companySnap.exists()) {
        const company = companySnap.data();
        setCompanyLink(`${window.location.origin}/cliente/${company.slug || companyId}`);
      }
    } catch (error) {
      console.error('Erro ao carregar link da empresa:', error);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;
    setLoading(true);
    let reservationsReady = false;
    let servicesReady = false;
    const finish = () => reservationsReady && servicesReady && setLoading(false);
    const unsubReservations = onSnapshot(collection(db, `companies/${companyId}/reservations`), snapshot => {
      setReservations(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
      reservationsReady = true;
      finish();
    }, error => {
      console.error('Erro ao acompanhar reservas:', error);
      reservationsReady = true;
      finish();
    });
    const unsubServices = onSnapshot(collection(db, `companies/${companyId}/services`), snapshot => {
      setServices(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
      servicesReady = true;
      finish();
    }, error => {
      console.error('Erro ao acompanhar serviços:', error);
      servicesReady = true;
      finish();
    });
    const unsubClients = onSnapshot(collection(db, `companies/${companyId}/clients`), snapshot => {
      const clients = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
      setActiveClients(groupClientsByIdentity(clients).filter(item => item.active !== false).length);
    }, error => console.error('Erro ao acompanhar clientes:', error));
    loadCompanyLink();
    return () => {
      unsubReservations();
      unsubServices();
      unsubClients();
    };
  }, [companyId, loadCompanyLink]);

  const analytics = useMemo(
    () => summarizePeriod(reservations, services, startDate, endDate),
    [endDate, reservations, services, startDate]
  );
  const todayKey = toDateKey(new Date());
  const todayAnalytics = useMemo(
    () => summarizePeriod(reservations, services, todayKey, todayKey),
    [reservations, services, todayKey]
  );
  const recentReservations = useMemo(() => analytics.reservations
    .filter(item => !['cancelled', 'canceled'].includes(item.status))
    .sort((a, b) => (toDate(b.date)?.getTime() || 0) - (toDate(a.date)?.getTime() || 0))
    .slice(0, 5), [analytics.reservations]);
  const checkedInPendingPayments = useMemo(() => reservations.filter(reservation => (
    reservation.checkInStatus === 'checked_in' &&
    reservation.paymentStatus !== 'paid' &&
    (Number(reservation.totalAmount) || 0) > (Number(reservation.paidAmount) || 0) &&
    !['cancelled', 'canceled'].includes(reservation.status)
  )), [reservations]);

  const selectPeriod = (next) => {
    setPeriod(next);
    if (next !== 'custom') {
      const range = presetRange(next);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };
  const copyCompanyLink = async () => {
    if (!companyLink) return;
    try {
      await navigator.clipboard.writeText(companyLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
    }
  };
  const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const percent = value => `${Number(value || 0).toFixed(1)}%`;
  const formatDate = value => {
    const date = toDate(value);
    return date ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  };
  const reservationStatus = status => ({
    pending: 'Pendente', confirmed: 'Confirmada', completed: 'Concluída',
    cancelled: 'Cancelada', canceled: 'Cancelada', no_show: 'Não compareceu'
  }[status] || status || 'Pendente');
  const paymentStatus = status => ({
    unpaid: 'Não pago', partial: 'Pagamento parcial', paid: 'Pago', refunded: 'Reembolsado'
  }[status] || status || 'Não informado');
  const stats = [
    { icon: Calendar, label: 'Reservas no período', value: analytics.reservationCount, color: '#2563eb' },
    { icon: CheckCircle2, label: 'Pessoas com check-in', value: analytics.checkedPeople, color: '#15803d' },
    { icon: BarChart3, label: 'Taxa de comparecimento', value: percent(analytics.checkInRate), color: '#0f766e' },
    { icon: TrendingDown, label: 'Taxa de desistência', value: percent(analytics.dropoutRate), color: '#b91c1c' },
    { icon: DollarSign, label: 'Receita confirmada', value: money(analytics.revenue), color: '#a16207' },
    { icon: Users, label: 'Clientes ativos', value: activeClients, color: '#7c3aed' }
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header dashboard-header--decision">
        <div><h1>{t('nav.dashboard')}</h1><p>Indicadores operacionais e comerciais para tomada de decisão</p></div>
        <div className="dashboard-period-control">
          <select value={period} onChange={event => selectPeriod(event.target.value)} aria-label="Período do painel">
            <option value="today">Hoje</option><option value="7days">Últimos 7 dias</option>
            <option value="30days">Últimos 30 dias</option><option value="month">Este mês</option>
            <option value="custom">Personalizado</option>
          </select>
          {period === 'custom' && <div className="dashboard-custom-dates">
            <input type="date" value={startDate} max={endDate} onChange={event => setStartDate(event.target.value)} aria-label="Data inicial" />
            <span>até</span>
            <input type="date" value={endDate} min={startDate} onChange={event => setEndDate(event.target.value)} aria-label="Data final" />
          </div>}
        </div>
      </div>

      {loading ? <LoadingState title="Carregando painel" subtitle="Calculando reservas e check-ins" /> : <>
        {checkedInPendingPayments.length > 0 && <section className="dashboard-payment-alert">
          <div className="dashboard-payment-alert-title">
            <AlertTriangle size={22} />
            <div><strong>Pagamento pendente após check-in</strong><span>{checkedInPendingPayments.length} reserva(s) aguardam lançamento.</span></div>
          </div>
          <div className="dashboard-payment-alert-list">
            {checkedInPendingPayments.slice(0, 4).map(reservation => (
              <div key={reservation.id}>
                <span><strong>{reservation.clientName || 'Cliente'}</strong>{reservation.serviceName || 'Serviço'}</span>
                <b>{money(Math.max(0, (reservation.totalAmount || 0) - (reservation.paidAmount || 0)))}</b>
                <button type="button" onClick={() => navigate(`/payments?reservation=${reservation.id}`)}>Lançar pagamento</button>
              </div>
            ))}
          </div>
          {checkedInPendingPayments.length > 4 && <button className="dashboard-payment-alert-all" type="button" onClick={() => navigate('/payments')}>Ver todas as pendências</button>}
        </section>}

        <div className="stats-grid stats-grid--decision">{stats.map(stat => (
          <div key={stat.label} className="stat-card" style={{ borderTopColor: stat.color }}>
            <div className="stat-icon" style={{ backgroundColor: stat.color }}><stat.icon size={22} color="white" /></div>
            <div className="stat-content"><p className="stat-label">{stat.label}</p><h2 className="stat-value">{stat.value}</h2></div>
          </div>
        ))}</div>

        <section className="today-operation-band">
          <div><span>Operação de hoje</span><strong>{todayAnalytics.expectedPeople}</strong><small>pessoas esperadas</small></div>
          <div><span>Check-ins feitos</span><strong>{todayAnalytics.checkedPeople}</strong><small>{percent(todayAnalytics.checkInRate)} de comparecimento</small></div>
          <div className={todayAnalytics.pendingCheckIns > 0 ? 'needs-attention' : ''}><span>Ainda faltam</span><strong>{todayAnalytics.pendingCheckIns}</strong><small>pessoas aguardando check-in</small></div>
          <div><span>Cancelamentos</span><strong>{todayAnalytics.cancelledCount}</strong><small>reservas canceladas hoje</small></div>
        </section>

        <div className="dashboard-decision-grid">
          <section className="decision-section decision-section--wide">
            <div className="decision-section-title"><div><span>Desempenho por serviço</span><h2>Compare operação e receita</h2></div><BarChart3 size={22} /></div>
            {analytics.byService.length === 0 ? <p className="decision-empty">Nenhum movimento no período selecionado.</p> :
              <div className="service-performance-table-wrap"><table className="service-performance-table">
                <thead><tr><th>Serviço</th><th>Reservas</th><th>Pessoas</th><th>Check-in</th><th>Canceladas</th><th>Receita</th></tr></thead>
                <tbody>{analytics.byService.map(service => <tr key={service.id}>
                  <td data-label="Serviço"><strong>{service.name}</strong></td><td data-label="Reservas">{service.reservations}</td><td data-label="Pessoas">{service.people}</td>
                  <td data-label="Check-in"><span className={`performance-rate ${service.checkInRate < 70 ? 'is-low' : ''}`}>{percent(service.checkInRate)}</span></td>
                  <td data-label="Canceladas">{service.cancellations}</td><td data-label="Receita"><strong>{money(service.revenue)}</strong></td>
                </tr>)}</tbody>
              </table></div>}
          </section>
          <section className="decision-section">
            <div className="decision-section-title"><div><span>Últimos movimentos</span><h2>Reservas do período</h2></div><Calendar size={22} /></div>
            {recentReservations.length === 0 ? <p className="decision-empty">Nenhuma reserva encontrada.</p> :
              <div className="decision-reservation-list">{recentReservations.map(reservation => <button
                className="decision-reservation"
                key={reservation.id}
                type="button"
                onClick={() => setSelectedReservation(reservation)}
                aria-label={`Ver detalhes da reserva de ${reservation.clientName || 'cliente'}`}
              >
                <div><strong>{reservation.clientName || 'Cliente'}</strong><span>{reservation.serviceName || 'Serviço'} · {formatDate(reservation.date)}</span></div>
                <span className={reservation.checkInStatus === 'checked_in' ? 'checked' : 'pending'}>{reservation.checkInStatus === 'checked_in' ? 'Check-in feito' : reservation.status === 'no_show' ? 'Não compareceu' : 'Aguardando'}</span>
              </button>)}</div>}
          </section>
        </div>

        {(analytics.dropoutRate >= 20 || todayAnalytics.pendingCheckIns > 0) && <section className="decision-alerts">
          <AlertTriangle size={22} /><div><strong>Pontos de atenção</strong><span>
            {analytics.dropoutRate >= 20 ? `Desistência em ${percent(analytics.dropoutRate)} no período. ` : ''}
            {todayAnalytics.pendingCheckIns > 0 ? `${todayAnalytics.pendingCheckIns} pessoa(s) ainda aguardam check-in hoje.` : ''}
          </span></div>
        </section>}
      </>}

      <div className="share-card share-card--compact"><div className="share-card__content">
        <div><p className="share-card__eyebrow">Canal de vendas</p><h3>Link público da empresa</h3><p className="share-link-inline">{companyLink || 'Carregando link...'}</p></div>
        <div className="share-card__actions">
          <a className="share-link" href={companyLink || '#'} target="_blank" rel="noreferrer"><ExternalLink size={16} />Abrir</a>
          <button className="share-copy" onClick={copyCompanyLink} type="button"><Copy size={16} />{copied ? 'Copiado!' : 'Copiar'}</button>
        </div>
      </div></div>

      {selectedReservation && <div className="reservation-detail-overlay" onClick={() => setSelectedReservation(null)}>
        <div
          className="reservation-detail-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reservation-detail-title"
          onClick={event => event.stopPropagation()}
        >
          <div className="reservation-detail-header">
            <div>
              <span>Detalhes da reserva</span>
              <h2 id="reservation-detail-title">{selectedReservation.clientName || 'Cliente'}</h2>
              <p>{selectedReservation.serviceName || 'Serviço não informado'}</p>
            </div>
            <button type="button" onClick={() => setSelectedReservation(null)} title="Fechar" aria-label="Fechar"><X size={21} /></button>
          </div>

          <div className="reservation-detail-statuses">
            <span className={`detail-status status-${selectedReservation.status || 'pending'}`}>{reservationStatus(selectedReservation.status)}</span>
            <span className={`detail-status payment-${selectedReservation.paymentStatus || 'unpaid'}`}>{paymentStatus(selectedReservation.paymentStatus)}</span>
            <span className={`detail-status checkin-${selectedReservation.checkInStatus || 'pending'}`}>
              {selectedReservation.checkInStatus === 'checked_in' ? 'Check-in realizado' : selectedReservation.status === 'no_show' ? 'Não compareceu' : 'Aguardando check-in'}
            </span>
          </div>

          <div className="reservation-detail-grid">
            <div><span>Data e horário</span><strong>{formatDate(selectedReservation.date) || 'Não informado'}</strong></div>
            <div><span>Participantes</span><strong>{selectedReservation.participants || selectedReservation.guests || 1}</strong></div>
            {selectedReservation.roomName && <div><span>Quarto</span><strong>{selectedReservation.roomName}</strong></div>}
            {selectedReservation.checkInDate && <div><span>Entrada</span><strong>{formatDate(selectedReservation.checkInDate)}</strong></div>}
            {selectedReservation.checkOutDate && <div><span>Saída</span><strong>{formatDate(selectedReservation.checkOutDate)}</strong></div>}
            <div><span>Valor total</span><strong>{money(selectedReservation.totalAmount)}</strong></div>
            <div><span>Valor pago</span><strong>{money(selectedReservation.paidAmount)}</strong></div>
            <div><span>Saldo pendente</span><strong>{money(Math.max(0, (selectedReservation.totalAmount || 0) - (selectedReservation.paidAmount || 0)))}</strong></div>
            <div><span>WhatsApp</span><strong>{selectedReservation.clientPhone || 'Não informado'}</strong></div>
            <div><span>E-mail</span><strong>{selectedReservation.clientEmail || 'Não informado'}</strong></div>
            <div><span>Criada por</span><strong>{selectedReservation.createdBy || selectedReservation.createdByEmail || 'Sistema'}</strong></div>
            <div><span>Código</span><strong className="reservation-detail-code">{selectedReservation.id}</strong></div>
          </div>

          {selectedReservation.notes && <div className="reservation-detail-notes"><span>Observações</span><p>{selectedReservation.notes}</p></div>}
        </div>
      </div>}
    </div>
  );
};

export default Dashboard;
